import React from 'react';

const PrintSummary = ({ data, impairmentProfile }) => (
  <div id="cp-print-summary">
    <div style={{ borderBottom: '2px solid #4f46e5', paddingBottom: 12, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>AssessMonk</div>
            <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Physiotherapy Assessment</div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>assessmonk.com</div>
      </div>
      <h1 style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 800 }}>🫀 CardioPulmonary Assessment</h1>
      <div style={{ fontSize: 11, color: '#64748b' }}>Printed: {new Date().toLocaleString()}</div>
    </div>

    {/* S1 – Patient Context */}
    <section style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f1f5f9', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>👤 Patient Context</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
        {[['Name', data.name], ['Age', data.age], ['Sex', data.sex], ['Occupation', data.occupation],
          ['Living Situation', data.livingSituation], ['Hosp No.', data.hospNo], ['Admission Date', data.doa], ['Ward', data.ward],
          ['Primary Diagnosis', data.primaryDiagnosis], ['Secondary Diagnoses', data.secondaryDiagnosis], ['Referral Reason', data.referralReason],
          ['O₂ / Ventilatory Device', data.oxygenDevice], ['Other Devices / Drains', data.drains], ['Medications', data.medications],
          ['Pre-Morbid Mobility', data.premorbidMobility], ['Pre-Morbid ADL', data.premorbidADL], ['Pre-Morbid Exercise', data.premorbidExercise],
          ['Discharge Destination', data.dischargeDestination], ['Mobility Target', data.dischargeMobilityTarget], ['Discharge Goals', data.dischargeGoals],
          ['Safe for PT', data.safeForPT], ['Temp', data.temp], ['PR', data.pr], ['BP', data.bp], ['RR', data.rr], ['SpO₂', data.spo2], ['Consciousness', data.consciousness],
          ['Safety Notes', data.redFlagNotes],
        ].filter(([, v]) => v).map(([l, v]) => (
          <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{Array.isArray(v) ? v.join(', ') : v}</div>
        ))}
      </div>
    </section>

    {/* S2 – Breathing */}
    {(data.breathingPattern || data.wob || data.accessoryMuscles?.length > 0 || data.breathSounds?.length > 0) && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#eff6ff', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>💨 Breathing Efficiency</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
          {[['Pattern', data.breathingPattern], ['WOB', data.wob], ['Observed RR', data.observedRR], ['Tripod Position', data.tripodPosition],
            ['Pursed-Lip Breathing', data.pursedLips], ['Accessory Muscles', data.accessoryMuscles?.join(', ')], ['Breath Sounds', data.breathSounds?.join(', ')],
            ['Breath Sounds Detail', data.breathSoundsDetails], ['Chest Symmetry', data.chestSymmetry], ['Upper Chest (cm)', data.chestMeasurementUpper],
            ['Lower Chest (cm)', data.chestMeasurementLower], ['Pain Limiting Breathing', data.painLimitingBreathing], ['Splinting', data.splinting],
            ['O₂ Delivery', data.oxygenStatus], ['O₂ Flow', data.oxygenFlow], ['Target SpO₂', data.targetSpo2Range], ['I:E Ratio', data.ieRatio],
            ['Impairments', data.breathingImpairments?.join(', ')], ['Notes', data.breathingNotes],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {/* S3 – Airway Clearance */}
    {(data.coughPresent || data.sputumPresent || data.aspirationRisk) && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f5f3ff', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🌊 Airway Clearance</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
          {[['Cough Present', data.coughPresent], ['Cough Strength', data.coughStrength], ['Cough Effectiveness', data.coughEffectiveness],
            ['Cough Type', data.coughType], ['Huffing', data.huffing], ['Assisted Cough', data.assistedCough],
            ['Sputum Present', data.sputumPresent], ['Sputum Colour', data.sputumColor], ['Sputum Consistency', data.sputumConsistency],
            ['Sputum Amount', data.sputumAmount], ['Blood in Sputum', data.bloodInSputum],
            ['Voice', data.voice], ['Swallowing', data.swallowing], ['Gag Reflex', data.gagReflex], ['Aspiration Risk', data.aspirationRisk],
            ['Secretion Management', data.secretionManagement], ['Impairments', data.clearanceImpairments?.join(', ')], ['Notes', data.clearanceNotes],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {/* S4 – Gas Exchange */}
    {(data.spo2Rest || data.cyanosis || data.abgPH) && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#ecfdf5', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🫁 Gas Exchange</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
          {[['SpO₂ Rest', data.spo2Rest], ['SpO₂ Sitting', data.spo2Sitting], ['SpO₂ Walking', data.spo2Walking], ['SpO₂ Recovery', data.spo2Recovery],
            ['Desat on Activity', data.desatOnActivity], ['Desat Amount', data.desatAmount ? `${data.desatAmount}%` : ''], ['Recovery Time', data.desatRecoveryTime],
            ['Cyanosis', data.cyanosis], ['Clubbing', data.clubbing], ['Confusion/Hypoxia', data.confusionHypoxia],
            ['Capillary Refill', data.capillaryRefill], ['Peripheral Pulses', data.peripheralPulses], ['Limb Warmth', data.limbWarmth],
            ['Percussion', data.percussionNote?.join(', ')], ['Fremitus', data.fremitus],
            ['ABG pH', data.abgPH], ['PaCO₂', data.abgPCO2], ['PaO₂', data.abgPO2], ['HCO₃', data.abgHCO3], ['BE', data.abgBE], ['FiO₂', data.abgFiO2],
            ['ABG Interpretation', data.abgInterpretation], ['Impairments', data.gasExchangeImpairments?.join(', ')], ['Notes', data.gasExchangeNotes],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {/* S5 – Cardiac */}
    {(data.heartRate || data.pr || data.jvp || data.nyha) && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#fef2f2', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🫀 Cardiovascular Tolerance</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
          {[['Heart Rate', data.heartRate || data.pr], ['BP', data.bp || (data.bpSystolic ? `${data.bpSystolic}/${data.bpDiastolic}` : '')],
            ['Rhythm', data.heartRhythm], ['Heart Sounds', data.heartSounds], ['Murmurs', data.murmurs],
            ['JVP', data.jvp], ['Oedema', data.oedemaPresent], ['Oedema Location', data.oedemaLocation?.join(', ')], ['Oedema Grade', data.oedemaGrade],
            ['NYHA Class', data.nyha], ['Orthopnoea', data.orthopnea], ['PND', data.pnd],
            ['Chest Pain', data.chestPainPresent], ['CP Quality', data.cpQual?.join(', ')], ['CP Radiation', data.cpRad], ['CP Severity', data.cpSeverity],
            ['HR with Activity', data.hrWithActivity], ['BP with Activity', data.bpWithActivity], ['Cardiac Recovery', data.cardiacRecovery],
            ['Stop Reason', data.cardiacStopReason], ['Echo EF', data.echoEF ? `${data.echoEF}%` : ''], ['Echo Findings', data.echoFindings],
            ['CXR', data.cxrFindings], ['ECG', data.ecgFindings], ['BNP', data.bnp], ['Troponin', data.troponin],
            ['FEV₁', data.fev1], ['FEV₁/FVC', data.fev1fvc], ['PFT Pattern', data.pftPattern], ['DLCO', data.dlco],
            ['Impairments', data.cardiacImpairments?.join(', ')], ['Notes', data.cardiacNotes],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {/* S6 – Functional Capacity */}
    {(data.currentMobilityLevel || data.mmrc || data.sixMWD) && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#fffbeb', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🏃 Functional Capacity</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
          {[['Mobility Level', data.currentMobilityLevel], ['Walking Distance', data.walkingDistance], ['Walking Aid', data.walkingAid],
            ['6MWD', data.sixMWD ? `${data.sixMWD}m` : ''], ['6MWD % Predicted', data.sixMWDpercent ? `${data.sixMWDpercent}%` : ''],
            ['mMRC', data.mmrc], ['Borg Baseline', data.borgBaseline], ['Borg Peak', data.borgPeak],
            ['Limiting Symptom', data.limitingSymptom], ['Limiting Domain', data.limitingDomain],
            ['ADL Independence', data.adlIndependence], ['Stairs', data.stairsCapacity],
            ['Pre HR', data.preHR], ['Peak HR', data.peakHR], ['Peak SpO₂', data.peakSPO2],
            ['1min HR Recovery', data.hrRecovery1min ? `${data.hrRecovery1min} bpm${data.abnormalHRRecovery ? ' ⚠️' : ' ✅'}` : ''],
            ['Discharge Readiness', data.dischargeReadiness], ['Notes', data.functionalNotes],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {/* S7 – Risk */}
    {(data.atelectasisRisk || data.pneumoniaRisk || data.dvtRisk) && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#fef2f2', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>⚠️ Risk & Complications</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '4px 16px' }}>
          {[['Atelectasis Risk', data.atelectasisRisk], ['Atelectasis Factors', data.atelectasisFactors?.join(', ')],
            ['Pneumonia Risk', data.pneumoniaRisk], ['Pneumonia Factors', data.pneumoniaFactors?.join(', ')],
            ['DVT Risk', data.dvtRisk], ['DVT Factors', data.dvtFactors?.join(', ')],
            ['Deconditioning Risk', data.deconditioningRisk], ['Deconditioning Factors', data.deconditioningFactors?.join(', ')],
            ['Fall Risk', data.fallRisk], ['Fall Factors', data.fallFactors?.join(', ')],
            ['Risk Notes', data.riskNotes],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {/* S8 – Treatment Plan */}
    {(data.ptImmediateGoals || data.ptShortTermGoals || data.ptTreatmentApproach?.length > 0) && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f1f5f9', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>📋 Treatment Plan</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '4px 16px' }}>
          {[['Priorities', data.ptPriorities?.join(', ')], ['Interventions', data.ptTreatmentApproach?.join(', ')],
            ['Immediate Goals', data.ptImmediateGoals], ['Short-Term Goals', data.ptShortTermGoals],
            ['Long-Term Goals', data.ptLongTermGoals], ['Progression Criteria', data.ptProgressionCriteria],
            ['Frequency', data.ptFrequency], ['Precautions', data.ptPrecautions], ['Plan Notes', data.planNotes],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 6 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {/* AI Profile summary */}
    {impairmentProfile && (
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#ecfdf5', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🤖 AI Impairment Profile</h2>
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: '0.72rem', color: '#92400e', lineHeight: 1.5 }}>
          <strong>AI-generated content — clinical review required.</strong> This assessment is produced by an AI model and may contain errors or omissions. It is intended to support, not replace, clinical judgement. Always verify findings against the patient's full clinical picture before acting.
        </div>
        {impairmentProfile.primaryImpairments?.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <strong>Primary Impairments:</strong>
            {impairmentProfile.primaryImpairments.map((imp, i) => (
              <div key={i} style={{ marginLeft: 12 }}>• {imp.domain} [{imp.severity}] — {imp.description}</div>
            ))}
          </div>
        )}
        {impairmentProfile.treatmentPrecautions?.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <strong>Treatment Precautions:</strong>
            {impairmentProfile.treatmentPrecautions.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}
          </div>
        )}
        {impairmentProfile.dischargeGoals?.length > 0 && (
          <div>
            <strong>Discharge Goals:</strong>
            {impairmentProfile.dischargeGoals.map((g, i) => <div key={i} style={{ marginLeft: 12 }}>• {g}</div>)}
          </div>
        )}
      </section>
    )}
  </div>
);

export default PrintSummary;
